import { useState, useRef, useEffect, type RefObject } from "react";
import cn from "mcn";

import { CloseIcon } from "../../icons/CloseIcon";
import { Button } from "../../ui/button";
import { useOptionalActivePageId, usePageProducts, useUpdatePageProducts, type TPageProduct } from "../../../data";
import { columnWidth as defaultColumnWidth, minColumnWidth, maxColumnWidth } from "../../globals";
import { textBase, textSm, textLg, cardContainer, controlContainer, type AnchorPosition } from "../../ui/shared-styles";
import { StepperButton } from "../../ui/stepper-button";

export function useSetAllColumnWidthsModal(
  getAnchorPosition: () => AnchorPosition,
  onPopupOpen?: () => void,
  onPopupClose?: () => void,
  closeMenu?: () => void,
  menuRef?: RefObject<HTMLDivElement>,
) {
  const [open, setOpen] = useState(false);
  const [anchorPosition, setAnchorPosition] = useState<AnchorPosition | null>(null);

  const openModal = () => {
    setAnchorPosition(getAnchorPosition());
    setOpen(true);
    onPopupOpen?.();
  };

  const closeModal = (shouldCloseMenu = true) => {
    setOpen(false);
    setAnchorPosition(null);
    onPopupClose?.();
    if (shouldCloseMenu) {
      closeMenu?.();
    }
  };

  const ColumnWidthsModal = open && anchorPosition && (
    <SetAllColumnWidthsModalContent
      onClose={() => closeModal(true)}
      onCloseKeepMenu={() => closeModal(false)}
      anchorPosition={anchorPosition}
      menuRef={menuRef}
    />
  );

  return { openModal, closeModal, ColumnWidthsModal };
}

function SetAllColumnWidthsModalContent({
  onClose,
  onCloseKeepMenu,
  anchorPosition,
  menuRef,
}: {
  onClose: () => void;
  onCloseKeepMenu: () => void;
  anchorPosition: AnchorPosition;
  menuRef?: RefObject<HTMLDivElement>;
}) {
  const [columnWidth, setColumnWidth] = useState(defaultColumnWidth);
  const [inputValue, setInputValue] = useState(String(defaultColumnWidth));
  const modalRef = useRef<HTMLDivElement>(null);

  const pageId = useOptionalActivePageId();
  const products = usePageProducts(pageId);
  const update = useUpdatePageProducts();

  const handleDecrease = () => {
    setColumnWidth((prev) => {
      const newValue = Math.max(minColumnWidth, prev - 1);
      setInputValue(String(newValue));
      return newValue;
    });
  };

  const handleIncrease = () => {
    setColumnWidth((prev) => {
      const newValue = Math.min(maxColumnWidth, prev + 1);
      setInputValue(String(newValue));
      return newValue;
    });
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value === "" || /^\d+$/.test(value)) {
      setInputValue(value);
    }
  };

  const handleInputBlur = () => {
    const num = parseInt(inputValue, 10);
    if (Number.isNaN(num) || inputValue === "") {
      setInputValue(String(columnWidth));
    } else {
      const clamped = Math.min(maxColumnWidth, Math.max(minColumnWidth, num));
      setColumnWidth(clamped);
      setInputValue(String(clamped));
    }
  };

  const handleApply = () => {
    const num = parseInt(inputValue, 10);
    const targetWidth = Number.isNaN(num) ? columnWidth : Math.min(maxColumnWidth, Math.max(minColumnWidth, num));
    onClose();
    products.data
      ?.filter((product: TPageProduct) => product.column_type === "product" || !product.column_type)
      .forEach((product: TPageProduct) => {
        update.optimistic({ id: product.id, column_width: targetWidth });
      });
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;

      if (modalRef.current?.contains(target)) {
        return;
      }

      if (menuRef?.current?.contains(target)) {
        onCloseKeepMenu();
        return;
      }

      onClose();
    };

    const timer = setTimeout(() => {
      document.addEventListener("mousedown", handleClickOutside);
    }, 0);

    return () => {
      clearTimeout(timer);
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [onClose, onCloseKeepMenu, menuRef]);

  const modalContent = (
    <div
      ref={modalRef}
      className={cn(
        "w-[216px] h-[160px] p-[var(--spacing-scale-medium-500,15px)]",
        cardContainer,
        "pointer-events-auto",
      )}
      style={{
        position: "fixed",
        bottom: anchorPosition.bottom,
        left: anchorPosition.left,
        zIndex: 9999,
      }}
      onClick={(e) => e.stopPropagation()}
    >
      <div className="flex flex-col gap-[var(--spacing-scale-small-400,12px)]">
        <div className="flex flex-col gap-px">
          <div className="flex items-center gap-[var(--spacing-scale-small-300,9px)]">
            <span className={cn("flex-1 text-[var(--foreground-default,#101010)] font-semibold", textLg)}>
              Set all column widths
            </span>
            <CloseIcon onClick={onClose} />
          </div>
          <span className={cn("text-[var(--foreground-muted,#808080)] font-medium", textSm)}>
            These changes will apply to all Curve columns on the current Page
          </span>
        </div>

        <div className="flex items-center gap-[var(--padding-small-300,9px)]">
          <span className={cn("flex-1 text-[var(--foreground-default,#101010)] font-medium", textBase)}>
            Column width
          </span>
          <div className={cn(controlContainer, "p-[var(--spacing-scale-small-spacing,3px)]")}>
            <StepperButton onClick={handleDecrease} icon="minus" />
            <input
              type="text"
              value={inputValue}
              onChange={handleInputChange}
              onBlur={handleInputBlur}
              className={cn(
                "w-[30px] font-medium text-[var(--foreground-default,#101010)] text-center bg-transparent border-none outline-none",
                textBase,
              )}
            />
            <StepperButton onClick={handleIncrease} icon="plus" />
          </div>
        </div>

        <div className="flex justify-end gap-4">
          <Button buttonType="cancel" onClick={onClose}>
            Cancel
          </Button>
          <Button buttonType="save" onClick={handleApply}>
            Apply
          </Button>
        </div>
      </div>
    </div>
  );

  return <div className="fixed inset-0 z-[9999] pointer-events-none">{modalContent}</div>;
}
